import React, { useState } from "react";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";

const Diagnostic = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    age: "",
    gender: "",
    phoneNumber: "",
    symptoms: [],
    duration: "",
    severity: "moderate",
    previousConditions: [],
    medications: "",
    location: "",
    travelHistory: "",
    environment: "",
    language: "francais"
  });

  const [selectedSymptoms, setSelectedSymptoms] = useState([]);

  const commonSymptoms = {
    generaux: [
      { id: "fievre", name: "Fièvre", icon: "🌡️" },
      { id: "fatigue", name: "Fatigue", icon: "😫" },
      { id: "mauxTete", name: "Maux de tête", icon: "🤕" },
      { id: "vertiges", name: "Vertiges", icon: "😵" },
      { id: "douleursCorp", name: "Douleurs corporelles", icon: "🤒" }
    ],
    digestifs: [
      { id: "nausees", name: "Nausées", icon: "🤢" },
      { id: "diarrhee", name: "Diarrhée", icon: "💩" },
      { id: "vomissements", name: "Vomissements", icon: "🤮" },
      { id: "douleursAbdo", name: "Douleurs abdominales", icon: "😣" }
    ],
    respiratoires: [
      { id: "toux", name: "Toux", icon: "😷" },
      { id: "difficulteRespi", name: "Difficulté à respirer", icon: "🫁" },
      { id: "malGorge", name: "Mal de gorge", icon: "😤" }
    ],
    cutanes: [
      { id: "eruption", name: "Éruption cutanée", icon: "🔴" },
      { id: "demangeaisons", name: "Démangeaisons", icon: "👆" },
      { id: "plaies", name: "Plaies ou blessures", icon: "🤕" }
    ]
  };

  const commonConditions = [
    { id: "paludisme", name: "Paludisme" },
    { id: "diabete", name: "Diabète" },
    { id: "hta", name: "Hypertension" },
    { id: "asthme", name: "Asthme" },
    { id: "drepanocytose", name: "Drépanocytose" },
    { id: "vih", name: "VIH/SIDA" },
    { id: "tuberculose", name: "Tuberculose" }
  ];

  const regions = [
    "Zone urbaine",
    "Zone rurale",
    "Zone forestière",
    "Zone désertique",
    "Zone côtière"
  ];

  const languages = [
    { id: "francais", name: "Français" },
    { id: "anglais", name: "English" },
    { id: "arabe", name: "العربية" },
    { id: "wolof", name: "Wolof" },
    { id: "bambara", name: "Bambara" },
    { id: "swahili", name: "Kiswahili" }
  ];

  const emergencyNumbers = [
    { name: "SAMU", number: "141", description: "Service d'Aide Médicale Urgente" },
    { name: "Pompiers", number: "18", description: "Urgences et secours" },
    { name: "Police", number: "17", description: "Urgences sécuritaires" },
    { name: "Numéro d'urgence international", number: "112", description: "Toutes urgences" },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const toggleSymptom = (symptomId) => {
    setSelectedSymptoms(prev => {
      if (prev.includes(symptomId)) {
        return prev.filter(id => id !== symptomId);
      } else {
        return [...prev, symptomId];
      }
    });
  };

  const handleConditionToggle = (condition) => {
    setFormData(prev => ({
      ...prev,
      previousConditions: prev.previousConditions.includes(condition)
        ? prev.previousConditions.filter(c => c !== condition)
        : [...prev.previousConditions, condition]
    }));
  };

  const handleNext = () => {
    if (step === 2) {
      setFormData(prev => ({
        ...prev,
        symptoms: selectedSymptoms
      }));
    }
    setStep((prev) => prev + 1);
  };

  const handlePrevious = () => {
    setStep((prev) => prev - 1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulation d'analyse des symptômes
    const urgentSymptoms = ["difficulteRespi", "douleursSeveres"];
    const hasUrgentSymptoms = selectedSymptoms.some(s => urgentSymptoms.includes(s));
    
    if (hasUrgentSymptoms) {
      setStep(5); // Afficher message d'urgence
    } else {
      setStep(4); // Afficher recommandations standard
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white shadow-xl rounded-lg overflow-hidden"
        >
          {/* Progress bar */}
          <div className="bg-blue-50 h-2">
            <div
              className="bg-blue-700 h-full transition-all duration-500"
              style={{ width: `${(step / 5) * 100}%` }}
            />
          </div>

          <div className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
              {step >= 4 ? "Diagnostic Terminé" : "Diagnostic Préliminaire En Ligne"}
            </h2>

            {step === 1 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-6"
              >
                <h3 className="text-xl font-semibold mb-4">
                  Informations personnelles
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Âge
                    </label>
                    <input
                      type="number"
                      name="age"
                      value={formData.age}
                      onChange={handleInputChange}
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Votre âge"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Genre
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Sélectionnez</option>
                      <option value="homme">Homme</option>
                      <option value="femme">Femme</option>
                    
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Environnement
                    </label>
                    <select
                      name="environment"
                      value={formData.environment}
                      onChange={handleInputChange}
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Sélectionnez votre zone</option>
                      {regions.map(region => (
                        <option key={region} value={region}>{region}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Langue préférée
                    </label>
                    <select
                      name="language"
                      value={formData.language}
                      onChange={handleInputChange}
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {languages.map(lang => (
                        <option key={lang.id} value={lang.id}>{lang.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Numéro de téléphone
                    </label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Votre numéro de téléphone"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <h3 className="text-xl font-semibold mb-6">
                  Sélectionnez vos symptômes
                </h3>
                
                {Object.entries(commonSymptoms).map(([category, symptoms]) => (
                  <div key={category} className="mb-6">
                    <h4 className="text-lg font-medium text-gray-700 mb-3 capitalize">
                      Symptômes {category}
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {symptoms.map(symptom => (
                        <button
                          key={symptom.id}
                          onClick={() => toggleSymptom(symptom.id)}
                          className={`p-3 rounded-lg border flex items-center space-x-2 transition-colors ${
                            selectedSymptoms.includes(symptom.id)
                              ? 'bg-blue-50 border-blue-500 text-blue-700'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <span>{symptom.icon}</span>
                          <span>{symptom.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Depuis combien de temps ?
                  </label>
                  <input
                    type="text"
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ex: 3 jours, 1 semaine..."
                  />
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-6"
              >
                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    Antécédents médicaux
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {commonConditions.map(condition => (
                      <button
                        key={condition.id}
                        onClick={() => handleConditionToggle(condition.id)}
                        className={`p-3 rounded-lg border transition-colors ${
                          formData.previousConditions.includes(condition.id)
                            ? 'bg-blue-50 border-blue-500 text-blue-700'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {condition.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    Médicaments actuels
                  </h3>
                  <textarea
                    name="medications"
                    value={formData.medications}
                    onChange={handleInputChange}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows="4"
                    placeholder="Listez les médicaments que vous prenez actuellement..."
                  />
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    Voyages récents
                  </h3>
                  <textarea
                    name="travelHistory"
                    value={formData.travelHistory}
                    onChange={handleInputChange}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    placeholder="Avez-vous voyagé récemment ? Si oui, où ?"
                  />
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center space-y-6"
              >
                <div className="text-6xl mb-4">✅</div>
                <h3 className="text-xl font-semibold mb-4">
                  Recommandations Préliminaires
                </h3>
                
                <div className="bg-blue-50 p-6 rounded-lg text-left">
                  <h4 className="font-semibold text-blue-900 mb-3">
                    Basé sur vos symptômes :
                  </h4>
                  <ul className="space-y-2 text-blue-800">
                    <li className="flex items-center">
                      <span className="mr-2">•</span>
                      Restez hydraté et reposez-vous
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">•</span>
                      Surveillez votre température
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">•</span>
                      Évitez les efforts physiques
                    </li>
                  </ul>
                </div>

                <div className="bg-green-50 p-6 rounded-lg text-left">
                  <h4 className="font-semibold text-green-900 mb-3">
                    Prochaines étapes :
                  </h4>
                  <ul className="space-y-2 text-green-800">
                    <li className="flex items-center">
                      <span className="mr-2">1.</span>
                      Un professionnel de santé vous contactera dans les 24h
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">2.</span>
                      Préparez vos questions pour la consultation
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">3.</span>
                      Gardez une trace de l'évolution de vos symptômes
                    </li>
                  </ul>
                </div>

                <div className="mt-8">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <div className="flex flex-col md:flex-row md:items-start gap-6">
                      <div className="flex-1">
                        <div className="flex items-center mb-3">
                          <svg className="h-6 w-6 text-yellow-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <p className="text-sm text-yellow-800 font-medium">
                            Important
                          </p>
                        </div>
                        <p className="text-sm text-yellow-700">
                          Ces recommandations sont données à titre indicatif.
                          En cas d'aggravation de vos symptômes, contactez immédiatement les services d'urgence.
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <h5 className="text-sm font-semibold text-gray-900 mb-3">Numéros d'urgence :</h5>
                        <div className="space-y-2">
                          {emergencyNumbers.map((contact) => (
                            <a
                              key={contact.number}
                              href={`tel:${contact.number}`}
                              className="flex items-center justify-between p-2 bg-white border rounded-md hover:bg-gray-50 transition-colors"
                            >
                              <span className="text-sm font-medium text-gray-900 mr-3">{contact.name}</span>
                              <span className="inline-flex items-center text-red-600">
                                <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                {contact.number}
                              </span>
                            </a>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 5 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-6"
              >
                <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-red-800">
                        Attention - Situation potentiellement urgente
                      </h3>
                      <p className="mt-2 text-red-700">
                        Vos symptômes peuvent nécessiter une attention médicale immédiate. Veuillez contacter un service d'urgence.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                  <div className="p-6">
                    <h4 className="text-xl font-semibold text-gray-900 mb-4">
                      Numéros d'urgence à contacter
                    </h4>
                    <div className="grid gap-4">
                      {emergencyNumbers.map((contact) => (
                        <div key={contact.number} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                          <div>
                            <h5 className="font-semibold text-gray-900">{contact.name}</h5>
                            <p className="text-gray-600 text-sm">{contact.description}</p>
                          </div>
                          <a
                            href={`tel:${contact.number}`}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            {contact.number}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {step < 4 && (
              <div className="mt-8 flex justify-between">
                {step > 1 && (
                  <button
                    onClick={handlePrevious}
                    className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Précédent
                  </button>
                )}
                <button
                  onClick={step === 3 ? handleSubmit : handleNext}
                  className={`ml-auto px-6 py-2 ${
                    step === 3
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  } text-white rounded-md transition-colors`}
                >
                  {step === 3 ? "Terminer" : "Suivant"}
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Diagnostic;